import { TaskState, type UserInfo } from "@iflyrpa/share";
import type { Response } from "playwright-core";
import { ExecutionState } from "../../types";
import { ProxyAgent } from "../../utils/proxy";
import type { SteelAction } from "./index";

const scanRetryMaxCount = 60; // 二维码扫描重试次数
const waitQrcodeResultMaxTime = scanRetryMaxCount * 2 * 1000; // 最长等待时间

export const rpaServer: SteelAction = async (task, params) => {
    const updateTaskState = task.taskStageStore?.update?.bind(task.taskStageStore, task.taskId || "");
    let executionState: ExecutionState | undefined;

    let proxyUrl: string | undefined;
    if (params.localIP) {
        const args: [string, string | undefined | null, number | undefined] = [
            params.localIP,
            params.proxyLoc,
            params.accountId,
        ];
        task.logger?.info(`==> 开始获取代理信息：${args}`);
        const ProxyAgentResult = await ProxyAgent({ logger: task.logger }, ...args);
        task.logger?.info("==> 代理信息获取成功！");
        proxyUrl = ProxyAgentResult ? `http://${ProxyAgentResult.ip}:${ProxyAgentResult.port}` : undefined;
    }

    const page = await task.createPage({
        url: "https://mp.weixin.qq.com/cgi-bin/loginpage",
        proxyUrl,
        userAgent: params.userAgent,
        ...(params.viewport ? { viewport: params.viewport } : {}),
    });

    task.steelBrowser?.on("disconnected", async () => {
        const browserContext = task.steelBrowserContext;
        const browser = task.steelBrowser;
        executionState = ExecutionState.BROWSER_CLOSED;
        await page.close();

        if (browserContext) {
            await browserContext.close();
        }
        if (browser) {
            await browser.close();
        }
    });

    await updateTaskState?.({
        state: TaskState.WAIT_SCAN,
        connectAddress: await task.steelConnector?.getLive(task.sessionId || ""),
        sessionId: task.sessionId,
    });

    let token: string | undefined;
    let userInfo: UserInfo = { uniqueId: "", avatar: "", name: "" };

    try {
        await new Promise<void>((resolve, reject) => {
            let finished = false;

            const timer = setTimeout(async () => {
                cleanup();
                executionState = ExecutionState.TIMEOUT;
                await updateTaskState?.({
                    state: TaskState.TIMEOUT,
                    error: "等待扫码登录超时",
                });
                resolve();
            }, waitQrcodeResultMaxTime);

            const cleanup = () => {
                if (finished) return; // 防止多次调用
                finished = true;
                clearTimeout(timer);
                page.off("response", handleAfterLogin);
                page.off("response", handleScanStatus);
                task.steelBrowser?.off("disconnected", handleBrowserClosed);
            };

            const handleBrowserClosed = () => {
                cleanup();
                executionState = ExecutionState.BROWSER_CLOSED;
                resolve();
            };

            const handleAfterLogin = async (resp: Response) => {
                try {
                    const url = resp.url();

                    // 登录成功跳转到公众号主页
                    if (url.includes("/wxamp/home")) {
                        cleanup();
                        await updateTaskState?.({
                            state: TaskState.CONFIRMED,
                        });

                        const tokenMatch = url.match(/token=(\d+)/);
                        if (tokenMatch) token = tokenMatch[1];

                        try {
                            await page.waitForFunction(() => !!(window as any).__INITIAL_STATE__?.userInfo, {
                                timeout: 10000,
                            });
                            const initialState = await page.evaluate(() => (window as any).__INITIAL_STATE__);

                            userInfo = {
                                name: initialState?.userInfo?.nickName ?? "",
                                uniqueId: initialState?.userInfo?.userName ?? "",
                                avatar: initialState?.userInfo?.iconUrl ?? "",
                            };
                        } catch (e) {
                            task.logger.warn("无法获取用户信息:", { error: e });
                        }

                        executionState = ExecutionState.SUCCESS;
                        resolve();
                    }
                } catch (err) {
                    cleanup();
                    reject(err);
                }
            };

            // 0=用户未扫码， 4=用户已扫码未确认， 1=用户已确认登录
            const handleScanStatus = async (response: Response) => {
                try {
                    const url = response.url();
                    if (!url.includes("/cgi-bin/scanloginqrcode?action=ask")) return;

                    let jsonResponse: Record<string, unknown>;
                    try {
                        const text = await response.text(); // 总是安全的
                        if (!text) {
                            task.logger.warn("Empty response body:", { url });
                            return;
                        }

                        jsonResponse = JSON.parse(text); // 手动解析
                    } catch (e) {
                        task.logger.warn("Failed to parse json:", { url, error: e });
                        return;
                    }
                    const status = jsonResponse.status;

                    // 状态码说明：
                    // 0=未扫码, 1=已确认登录, 4=已扫码未确认
                    if (status === 1 || status === 4) {
                        if (status === 4) {
                            await updateTaskState?.({
                                state: TaskState.SCANNED,
                            });
                        }

                        page.off("response", handleScanStatus);
                        page.on("response", handleAfterLogin);
                    }
                } catch (err) {
                    cleanup();
                    reject(err);
                }
            };

            // 在页面加载之前就注册监听，防止丢失请求
            task.steelBrowser?.on("disconnected", handleBrowserClosed);
            page.on("response", handleScanStatus);
        });
    } finally {
        await page.close();
    }

    if (executionState === undefined) return { code: 500, message: "未知错误", data: {} };

    switch (executionState) {
        case ExecutionState.BROWSER_CLOSED: {
            const msg = "浏览器已关闭，任务中断!";
            await updateTaskState?.({
                state: TaskState.FAILED,
                error: "浏览器已关闭",
            });
            return { code: 414, message: msg, data: {} };
        }

        case ExecutionState.TIMEOUT: {
            const msg = "等待用户扫码登录超时!";
            await updateTaskState?.({
                state: TaskState.TIMEOUT,
                error: msg,
            });
            task.logger.info(msg);
            return { code: 414, message: msg, data: {} };
        }

        case ExecutionState.SUCCESS:
            await updateTaskState?.({
                state: TaskState.SUCCESS,
                result: {
                    cookie: JSON.stringify(await task.steelBrowserContext?.cookies()),
                    token,
                    userInfo: userInfo,
                },
            });
            return {
                code: 0,
                message: "成功",
                data: {
                    cookie: JSON.stringify(await task.steelBrowserContext?.cookies()),
                    token,
                    userInfo,
                },
            };
        default:
            // 处理 RUNNING 和其他可能的状态
            return { code: 500, message: "任务执行中断", data: {} };
    }
};
